/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  Badge,
  HStack,
  IconButton,
  Tooltip,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  VStack,
} from '@chakra-ui/react';
import { Plus, PencilSimple, Trash } from 'phosphor-react';
import { toast } from 'sonner';
import moment from 'moment';
import { useRegimesEspeciais } from '../../../../../../hooks/useRegimesEspeciais';
import { IRegimeEspecial } from '../../../../../../types/regimeEspecial';
import { formatCurrency, toNumber } from '../../../../../../utils/FormatFunctions';
import { RegimeEspecialFormModal } from './RegimeEspecialFormModal';

interface RegimeEspecialEmpresaPanelProps {
  empresaId: number;
}

const esferaColor: Record<string, string> = {
  federal: 'blue',
  estadual: 'purple',
  municipal: 'green',
};

const statusColor: Record<string, string> = {
  ativo: 'green',
  suspenso: 'yellow',
  encerrado: 'red',
};

export function RegimeEspecialEmpresaPanel({ empresaId }: RegimeEspecialEmpresaPanelProps) {
  const queryClient = useQueryClient();
  const { getRegimesEspeciaisEmpresa, deleteRegimeEspecial } = useRegimesEspeciais();

  const formModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState<IRegimeEspecial | null>(null);
  const [deleting, setDeleting] = useState<IRegimeEspecial | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['regimes-especiais-empresa', { empresa_id: empresaId }],
    queryFn: getRegimesEspeciaisEmpresa,
    enabled: !!empresaId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (row: IRegimeEspecial) => deleteRegimeEspecial(empresaId, row.id),
    onSuccess: () => {
      toast.success('Regime removido');
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais-empresa'] });
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais'] });
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais-kpis'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao remover. Tente novamente.');
    },
  });

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" color="gray.600">
          {isLoading
            ? 'Carregando regimes especiais...'
            : `${data?.length ?? 0} regime(s) cadastrado(s) para esta empresa.`}
        </Text>
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<Plus size={16} />}
          onClick={() => {
            setEditing(null);
            formModal.onOpen();
          }}
        >
          Novo regime
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center" py={6}>
          <Spinner color="blue.500" />
        </Flex>
      ) : !data || data.length === 0 ? (
        <Box py={6} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="md">
          <Text color="gray.500" fontSize="sm">
            Nenhum regime especial cadastrado.
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={2}>
          {data.map(row => (
            <Flex
              key={row.id}
              p={3}
              borderRadius="md"
              border="1px"
              borderColor="gray.200"
              bg="white"
              justify="space-between"
              align="center"
              gap={2}
            >
              <Box flex={1}>
                <HStack mb={1} spacing={2}>
                  <Badge colorScheme={esferaColor[row.esfera] ?? 'gray'} fontSize="9px">
                    {row.esfera}
                  </Badge>
                  <Badge colorScheme={statusColor[row.status] ?? 'gray'} fontSize="9px">
                    {row.status}
                  </Badge>
                  <Text fontWeight="medium" fontSize="13px">
                    {row.tipo}
                  </Text>
                  {row.numero_ato && (
                    <Text fontSize="11px" color="gray.500">
                      · ato {row.numero_ato}
                    </Text>
                  )}
                </HStack>
                <Text fontSize="11px" color="gray.600">
                  {row.emissor ? `${row.emissor}` : ''}
                  {row.uf ? ` · ${row.uf}` : ''}
                  {row.dt_inicio ? ` · ${moment(row.dt_inicio).format('DD/MM/YYYY')}` : ''}
                  {row.dt_fim ? ` → ${moment(row.dt_fim).format('DD/MM/YYYY')}` : ''}
                </Text>
                {row.economia_estimada !== undefined && row.economia_estimada !== null && (
                  <Text fontSize="11px" color="gray.500" mt={1}>
                    Economia estimada:{' '}
                    <strong>{formatCurrency(toNumber(row.economia_estimada))}</strong>
                  </Text>
                )}
              </Box>
              <HStack>
                <Tooltip label="Editar">
                  <IconButton
                    aria-label="Editar"
                    icon={<PencilSimple size={14} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      setEditing(row);
                      formModal.onOpen();
                    }}
                  />
                </Tooltip>
                <Tooltip label="Remover">
                  <IconButton
                    aria-label="Remover"
                    icon={<Trash size={14} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      setDeleting(row);
                      deleteDialog.onOpen();
                    }}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          ))}
        </VStack>
      )}

      {formModal.isOpen && (
        <RegimeEspecialFormModal
          isOpen={formModal.isOpen}
          onClose={formModal.onClose}
          empresaId={empresaId}
          regime={editing}
        />
      )}

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remover regime especial
            </AlertDialogHeader>
            <AlertDialogBody>
              Confirmar remoção do regime <strong>{deleting?.tipo}</strong>
              {deleting?.numero_ato ? ` (ato ${deleting.numero_ato})` : ''}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={deleteMutation.isPending}
                onClick={() => {
                  if (deleting) {
                    deleteMutation.mutate(deleting, {
                      onSuccess: () => deleteDialog.onClose(),
                    });
                  }
                }}
              >
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
